<script setup lang="ts">
import { Map, Marker, LngLatBounds } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { getMapStyleUrl } from './aws-location.config';
import type { Coordinates } from './maps.interface';

interface Props {
   locations: Coordinates[];
   zoom?: number;
}

const props = withDefaults(defineProps<Props>(), {
   zoom: 12,
});

const mapContainer = ref<HTMLElement>();
let mapInstance: Map | undefined;
let markers: Marker[] = [];

const centerOn = (lng: number, lat: number): void => {
   mapInstance?.flyTo({
      center: [lng, lat],
      zoom: 12, // zoom in when centering a specific location
      duration: 300,
   });
};

// uses first location as initial center
const getCenter = (locations: Coordinates[]): [number, number] => {
   if (locations.length === 0) {
      return [0, 0];
   }

   const first = locations[0];
   return [first.lng, first.lat];
};

const addMarkersToMap = (map: Map, locations: Coordinates[]): void => {
   // clear markers
   markers.forEach((marker) => marker.remove());
   markers = [];

   // add markers
   locations.forEach((location) => {
      const marker = new Marker({ color: 'var(--nxt-dark)' }).setLngLat([location.lng, location.lat]).addTo(map);
      markers.push(marker);
   });
};

const fitMapToBounds = (map: Map, locations: Coordinates[]): void => {
   if (locations.length === 0) return;

   // only 1 location, center on it
   if (locations.length === 1) {
      map.setCenter([locations[0].lng, locations[0].lat]);
      map.setZoom(props.zoom);
      return;
   }

   // bounds/map zoom to include all locations
   const bounds = new LngLatBounds();
   locations.forEach((location) => {
      bounds.extend([location.lng, location.lat]);
   });

   // fit map to show all locations
   map.fitBounds(bounds, {
      padding: { top: 50, bottom: 50, left: 50, right: 50 }, // avoid cutting off marker design if on edge
      duration: 300, // make it quick
   });
};

const updateMapWithMarkers = (map: Map, locations: Coordinates[]): void => {
   if (locations.length === 0) return;

   addMarkersToMap(map, locations);
   fitMapToBounds(map, locations);
};

onMounted(() => {
   if (!mapContainer.value) return;

   const map = new Map({
      zoom: props.zoom,
      interactive: true,
      container: mapContainer.value,
      maplibreLogo: false,
      attributionControl: false,
      style: getMapStyleUrl(),
      center: [0, 0], // will be centered when markers come available
   });

   mapInstance = map;

   map.on('load', () => {
      updateMapWithMarkers(map, props.locations); // set markers and re-center
   });
});

watch(
   () => props.locations,
   (newLocations) => {
      if (!mapInstance) return;
      updateMapWithMarkers(mapInstance, newLocations);
   },
   { deep: true },
);

onUnmounted(() => {
   markers.forEach((marker) => marker.remove());
   markers = [];
   mapInstance?.remove();
   mapInstance = undefined;
});

defineExpose({
   centerOn,
});
</script>

<template>
   <div ref="mapContainer" class="map-container"></div>
</template>

<style scoped lang="scss">
.map-container {
   position: relative;
   width: 100%;
   height: 100%;
   background: var(--nxt-light-grey);
}
</style>
